import * as Yup from 'yup';

import type { LeadConditionReportSchemaResponse } from '../../../../../services/marketplace/generated/kubb/models/LeadConditionReportSchemaResponse';
import type { PatchLeadByExternalIdDataMutationRequest } from '../../../../../services/marketplace/generated/kubb/models/PatchLeadByExternalIdData';

export type SchemaStep = LeadConditionReportSchemaResponse['steps'][number];
export type SchemaQuestion = SchemaStep['questions'][number];
export type WriteAnswer = NonNullable<
  PatchLeadByExternalIdDataMutationRequest['conditionReport']
>[number];

export const MIN_TEXT_LENGTH = 20;
export const MAX_TEXT_LENGTH = 500;

export type MultiValue = { issues: string[]; noIssues: boolean };

export const isMultiValue = (v: unknown): v is MultiValue =>
  typeof v === 'object' && v !== null && 'issues' in v;

export const allActiveQuestions = (steps: SchemaStep[]): SchemaQuestion[] =>
  steps.flatMap((s) => s.questions.filter((q) => q.active));

export const labelForValue = (options: SchemaQuestion['options'], value: string): string =>
  options?.find((o) => o.value === value)?.label ?? value;

export const isQuestionVisible = (q: SchemaQuestion, values: Record<string, unknown>): boolean =>
  q.showWhen == null || q.showWhen.values.includes(String(values[q.showWhen.questionId] ?? ''));

/** `{answer}` in a label = the picked option label of the showWhen parent, lowercased. */
export const resolveLabel = (
  q: SchemaQuestion,
  parent: SchemaQuestion,
  values: Record<string, unknown>,
): string =>
  q.label.replace(
    '{answer}',
    labelForValue(parent.options, String(values[parent.questionId] ?? '')).toLowerCase(),
  );

export const encodeAnswers = (
  values: Record<string, unknown>,
  questions: SchemaQuestion[],
): WriteAnswer[] => {
  const out: WriteAnswer[] = [];

  questions.forEach((q) => {
    if (!q.active || !isQuestionVisible(q, values)) return;

    const raw = values[q.questionId];

    if (q.type === 'multi_select') {
      if (isMultiValue(raw)) {
        if (raw.noIssues) {
          const exclusiveValue = q.options?.find((o) => o.exclusive)?.value;
          if (exclusiveValue) out.push({ questionId: q.questionId, value: exclusiveValue });
        } else {
          raw.issues.forEach((v) => out.push({ questionId: q.questionId, value: v }));
        }
      }
      return;
    }

    if (raw == null || raw === '') return;

    if (q.type === 'number') {
      const num = typeof raw === 'number' ? raw : Number(raw);
      if (Number.isFinite(num)) out.push({ questionId: q.questionId, value: num });
      return;
    }

    if (q.type === 'boolean') {
      out.push({ questionId: q.questionId, value: Boolean(raw) });
      return;
    }

    out.push({ questionId: q.questionId, value: String(raw) });
  });

  return out;
};

export const decodeAnswers = (steps: SchemaStep[]): Record<string, unknown> => {
  const out: Record<string, unknown> = {};

  steps.forEach((step) => {
    step.questions.forEach((q) => {
      if (q.answers.length === 0) return;

      if (q.type === 'multi_select') {
        const exclusiveValue = q.options?.find((o) => o.exclusive)?.value;
        const values = q.answers.map((a) => String(a.value));
        if (exclusiveValue && values.includes(exclusiveValue)) {
          out[q.questionId] = { issues: [], noIssues: true };
        } else {
          out[q.questionId] = { issues: values, noIssues: false };
        }
        return;
      }

      const latest = q.answers.reduce((a, b) => (a.answeredAt > b.answeredAt ? a : b));
      const raw = latest.value;

      if (q.type === 'boolean') {
        out[q.questionId] = raw === true || raw === 'true';
        return;
      }

      if (q.type === 'number') {
        const num = typeof raw === 'number' ? raw : Number(raw);
        out[q.questionId] = Number.isFinite(num) ? num : '';
        return;
      }

      out[q.questionId] = raw;
    });
  });

  return out;
};

export const buildInitialValues = (steps: SchemaStep[]): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  allActiveQuestions(steps).forEach((q) => {
    switch (q.type) {
      case 'multi_select':
        out[q.questionId] = { issues: [], noIssues: false };
        break;
      default:
        out[q.questionId] = '';
    }
  });
  return out;
};

export const isQuestionAnswered = (q: SchemaQuestion, values: Record<string, unknown>): boolean => {
  if (!isQuestionVisible(q, values)) return true;
  const raw = values[q.questionId];
  if (q.type === 'multi_select') {
    return isMultiValue(raw) && (raw.issues.length > 0 || raw.noIssues);
  }
  if (q.type === 'boolean') return typeof raw === 'boolean';
  if (q.type === 'number') return typeof raw === 'number' && Number.isFinite(raw);
  if (typeof raw !== 'string') return false;
  return q.type === 'text' ? raw.trim().length >= MIN_TEXT_LENGTH : raw.length > 0;
};

export const isStepComplete = (step: SchemaStep, values: Record<string, unknown>): boolean =>
  step.questions.filter((q) => q.active).every((q) => isQuestionAnswered(q, values));

export const firstIncompleteStepIndex = (
  steps: SchemaStep[],
  values: Record<string, unknown>,
): number => {
  const idx = steps.findIndex((s) => !isStepComplete(s, values));
  return idx === -1 ? steps.length : idx;
};

export const stepsCompleteCount = (steps: SchemaStep[], values: Record<string, unknown>): number =>
  steps.filter((s) => isStepComplete(s, values)).length;

/** NEXT stays off until every visible text question has MIN_TEXT_LENGTH+ chars (design: no error-on-click for the why box). */
export const stepHasUnansweredText = (step: SchemaStep, values: Record<string, unknown>): boolean =>
  step.questions.some((q) => q.active && q.type === 'text' && !isQuestionAnswered(q, values));

const multiSelectSchema = Yup.object({
  issues: Yup.array().of(Yup.string()),
  noIssues: Yup.boolean(),
}).test(
  'at-least-one',
  'Please select at least one option',
  (value) => (value?.issues?.length ?? 0) > 0 || value?.noIssues === true,
);

const schemaForQuestion = (q: SchemaQuestion): Yup.AnySchema => {
  switch (q.type) {
    case 'boolean':
      return Yup.boolean().required('Please select an option').typeError('Please select an option');
    case 'enum':
      return Yup.string()
        .required('Please select an option')
        .oneOf((q.options ?? []).map((o) => o.value));
    case 'multi_select':
      return multiSelectSchema;
    case 'number':
      return Yup.number()
        .required('Please enter a value')
        .positive('Must be a positive number')
        .typeError('Please enter a number');
    case 'text': {
      const text = Yup.string().trim().max(MAX_TEXT_LENGTH);
      const filled = (s: Yup.StringSchema) =>
        s
          .required('Please tell us why')
          .min(MIN_TEXT_LENGTH, `Please write at least ${MIN_TEXT_LENGTH} characters`);
      if (q.showWhen == null) return filled(text);
      const { questionId, values } = q.showWhen;
      return text.when(questionId, {
        is: (v: unknown) => values.includes(String(v)),
        then: filled,
      });
    }
    default:
      return Yup.mixed().notRequired();
  }
};

export const buildValidationSchema = (steps: SchemaStep[]) => {
  const shape: Record<string, Yup.AnySchema> = {};
  allActiveQuestions(steps).forEach((q) => {
    shape[q.questionId] = schemaForQuestion(q);
  });
  return Yup.object(shape);
};

export const collectInvalidSectionLabels = (
  step: SchemaStep,
  errors: Record<string, unknown>,
  touched: Record<string, unknown>,
): string[] =>
  step.questions
    .filter(
      (q) =>
        q.active &&
        q.type !== 'text' &&
        touched[q.questionId] != null &&
        errors[q.questionId] != null,
    )
    .map((q) => q.label);

export const buildMissingSectionsMessage = (labels: string[]): string | null => {
  if (labels.length === 0) return null;
  if (labels.length === 1) {
    return `Please select at least one option in the ${labels[0]} section above.`;
  }
  return 'Some sections above are missing answers.';
};
